import { apiOrigin } from '@/lib/config'
import { apiFetch } from './http'

/**
 * API base URL - all runs/artifacts calls go through orchestrator YARP proxy
 */
const API_BASE_URL = `${apiOrigin()}/api`

/**
 * Custom error for API responses
 */
export class ApiError extends Error {
  /**
   * The server's refusals by field, from a FastEndpoints error body (`{ errors: { field: [messages] } }`).
   * Empty when the body carries none.
   */
  public readonly fieldErrors: Record<string, string[]>

  /**
   * When the body says what was refused, that becomes the message: the orchestrator's validation failures
   * arrive in `errors`, beside a generic `message`, and reading only `error`/`detail` showed every one of
   * them as "HTTP error 400". Otherwise the caller's message stands.
   */
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public body?: unknown
  ) {
    super(describeFieldErrors(fieldErrorsFrom(body)) ?? message)
    this.name = 'ApiError'
    this.fieldErrors = fieldErrorsFrom(body)
  }

  /** The first message for `field`, matched without regard to case; undefined when it has none. */
  fieldError(field: string): string | undefined {
    const key = Object.keys(this.fieldErrors).find((k) => k.toLowerCase() === field.toLowerCase())
    return key === undefined ? undefined : this.fieldErrors[key][0]
  }
}

/** The `errors` map of an error body, keeping only string messages. */
export function fieldErrorsFrom(body: unknown): Record<string, string[]> {
  if (typeof body !== 'object' || body === null || !('errors' in body)) {
    return {}
  }

  const errors = (body as { errors: unknown }).errors
  if (typeof errors !== 'object' || errors === null || Array.isArray(errors)) {
    return {}
  }

  const result: Record<string, string[]> = {}
  for (const [field, messages] of Object.entries(errors as Record<string, unknown>)) {
    const texts = (Array.isArray(messages) ? messages : [messages]).filter(
      (m): m is string => typeof m === 'string' && m.trim().length > 0
    )
    if (texts.length > 0) {
      result[field] = texts
    }
  }
  return result
}

/** Every distinct message, general ones first, as one sentence-separated line; undefined when there are none. */
function describeFieldErrors(errors: Record<string, string[]>): string | undefined {
  const isGeneral = (field: string) => field.toLowerCase() === 'generalerrors'
  const ordered = [
    ...Object.entries(errors).filter(([field]) => isGeneral(field)),
    ...Object.entries(errors).filter(([field]) => !isGeneral(field)),
  ]
  const messages = [...new Set(ordered.flatMap(([, texts]) => texts))]
  return messages.length > 0 ? messages.join(' ') : undefined
}

/**
 * Request options for API calls
 */
interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** Request body (will be JSON stringified) */
  body?: unknown
}

/**
 * Make an authenticated API request
 * 
 * @param endpoint - API endpoint (will be appended to base URL)
 * @param options - Request options
 * @returns Parsed JSON response
 * @throws ApiError on non-2xx responses
 * 
 * @example
 * ```ts
 * // GET request
 * const runs = await apiClient('/runs')
 * 
 * // POST request with body
 * const newRun = await apiClient('/runs', {
 *   method: 'POST',
 *   body: { goal: 'Fix bug', repository_url: 'https://...' }
 * })
 * ```
 */
export async function apiClient<T = unknown>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, headers: customHeaders, ...fetchOptions } = options

  // Build headers — only set Content-Type when there is a body
  const headers: Record<string, string> = {
    ...(body != null ? { 'Content-Type': 'application/json' } : {}),
    ...(customHeaders as Record<string, string>),
  }

  // Build URL
  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${API_BASE_URL}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`

  // Make request. Authentication is the session cookie, attached by `apiFetch` along with the CSRF
  // header a state-changing request needs — there is no token here to attach and none to forget.
  const response = await apiFetch(url, {
    ...fetchOptions,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  // Handle non-2xx responses
  if (!response.ok) {
    let errorBody: unknown
    try {
      errorBody = await response.json()
    } catch {
      // Response body is not JSON
    }

    const message = typeof errorBody === 'object' && errorBody !== null && 'detail' in errorBody
      ? String((errorBody as { detail: unknown }).detail)
      : `${response.status} ${response.statusText}`

    throw new ApiError(message, response.status, response.statusText, errorBody)
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T
  }

  // Parse JSON response
  return response.json()
}

/**
 * GET request helper
 */
export function get<T = unknown>(endpoint: string, options?: RequestOptions): Promise<T> {
  return apiClient<T>(endpoint, { ...options, method: 'GET' })
}

/**
 * POST request helper
 */
export function post<T = unknown>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return apiClient<T>(endpoint, { ...options, method: 'POST', body })
}

/**
 * PUT request helper
 */
export function put<T = unknown>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return apiClient<T>(endpoint, { ...options, method: 'PUT', body })
}

/**
 * DELETE request helper
 */
export function del<T = unknown>(endpoint: string, options?: RequestOptions): Promise<T> {
  return apiClient<T>(endpoint, { ...options, method: 'DELETE' })
}
